import React, { createContext, useContext, useState, ReactNode, useEffect, ComponentType } from 'react';
import useSetParamsURL from '../../../lib/useSetParamsURL';

interface BaseModalProps {
    open: boolean;
    handleClose: () => void;
}

interface GenericModalContextType<T = any> {
    openModal: (content: T) => void;
    closeModal: () => void;
}

interface GenericModalProviderProps<T = any> {
    children: ReactNode;
    ModalComponent: ComponentType<BaseModalProps & { value: T }>;
    urlParam: string;
    contextName?: string;
}

export function createGenericModalContext<T>() {
    const GenericModalContext = createContext<GenericModalContextType<T> | undefined>(undefined);

    const GenericModalProvider: React.FC<GenericModalProviderProps<T>> = ({ 
        children, 
        ModalComponent, 
        urlParam
    }) => {
        const { searchParams, clearSingleParamUrl } = useSetParamsURL();
        const codigo = searchParams.get(urlParam);

        const [isOpen, setIsOpen] = useState(Boolean(codigo));
        const [itemValue, setItemValue] = useState<T | null>(codigo as T);

        const openModal = (content: T) => {
            setItemValue(content);
            setIsOpen(true);
        };

        const closeModal = () => {
            setIsOpen(false);
            setItemValue(null);
            clearSingleParamUrl(urlParam);
        };

        useEffect(() => {
            if (codigo) setItemValue(codigo as T);
        }, [codigo]);

        return (
            <GenericModalContext.Provider value={{ openModal, closeModal }}>
                {children}

                {
                    (itemValue && isOpen) &&
                    <ModalComponent
                        open={isOpen}
                        value={itemValue}
                        handleClose={closeModal}
                    />
                }
            </GenericModalContext.Provider>
        );
    };

    const useGenericModal = (): GenericModalContextType<T> => {
        const context = useContext(GenericModalContext);
        if (!context) {
            throw new Error(`useGenericModal deve ser usado dentro de um GenericModalProvider`);
        }
        return context;
    };

    return {
        GenericModalProvider,
        useGenericModal
    };
}

export interface IDialogContextGeneric<T> {
    open: boolean;
    value: T;
    handleClose: () => void;
}